import { featureCollection, point } from '@turf/helpers';
import { Map } from 'component/Map';
import { React, useEffect, useState, useCallback } from 'react';
import NTSPoint from 'assets/images/icons/location.png';
import { convertCoords3857To4326, convertCoords4326To3857 } from 'utils/Helper';
import { useBufferZone } from 'hooks/useBufferZone';
import { HASwitchers } from 'component/HASwitchers';

export const NTSmap = (props) => {
  const [NTSgeojson, setNTSgeojson] = useState(featureCollection([]));

  const [coords4326, setCoords4326] = useState(
    props?.formData?.info?.point?.geometry?.coordinates || null
  );

  useBufferZone(props?.map);

  useEffect(() => {
    setCoords4326(props?.formData?.info?.point?.geometry?.coordinates);
  }, [props?.formData?.info?.point?.geometry?.coordinates]);

  const handleKeyDown = (e) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    const isNumber = /^[0-9]$/.test(e.key);
    const isAllowedKey = allowedKeys.includes(e.key);
    const isCtrlA = e.ctrlKey && e.key === 'a';

    if (!isNumber && !isAllowedKey && !isCtrlA) {
      e.preventDefault();
      return;
    }

    const isTextSelected = e.target.selectionStart !== e.target.selectionEnd;

    if (isNumber && e.target.value.length >= 8 && !isTextSelected) {
      e.preventDefault();
    }
  };

  const handleLong = useCallback(
    (value) => {
      const numericValue = value === '' ? null : Number(value);
      if (isNaN(numericValue) && numericValue !== null) return;

      props.setCoords3857((prevCoords) => {
        if (prevCoords[0] === numericValue) return prevCoords;
        const updatedCoords = [...prevCoords];
        updatedCoords[0] = numericValue;
        return updatedCoords;
      });
    },
    [props?.setCoords3857]
  );

  const handleLat = useCallback(
    (value) => {
      const numericValue = value === '' ? null : Number(value);
      if (isNaN(numericValue) && numericValue !== null) return;

      props.setCoords3857((prevCoords) => {
        if (prevCoords[1] === numericValue) return prevCoords;
        const updatedCoords = [...prevCoords];
        updatedCoords[1] = numericValue;
        return updatedCoords;
      });
    },
    [props?.setCoords3857]
  );

  useEffect(() => {
    if (props?.mode === 'edit') return;
    setNTSgeojson(featureCollection([]));
    setCoords4326(null);
    handleLat(null);
    handleLong(null);
  }, [props?.formData?.info?.general?.hazard_name, props?.mode, handleLat, handleLong]);

  const updateFormDataWithCoords = useCallback(
    (coords) => {
      if (
        coords &&
        coords[0] !== null &&
        coords[1] !== null &&
        !isNaN(coords[0]) &&
        !isNaN(coords[1])
      ) {
        props.setFormData((prevFormData) => {
          const currentCoords = prevFormData?.info?.point?.coordinates;

          if (
            currentCoords &&
            Math.abs(currentCoords[0] - coords[0]) < 0.01 &&
            Math.abs(currentCoords[1] - coords[1]) < 0.01
          ) {
            return prevFormData;
          }
          return {
            ...prevFormData,
            info: {
              ...prevFormData?.info,
              point: {
                type: 'Point',
                coordinates: coords,
              },
            },
          };
        });
      }
    },
    [props?.setFormData]
  );

  const updateGeojson = useCallback(() => {
    if (
      coords4326 &&
      coords4326[0] !== null &&
      coords4326[1] !== null &&
      !isNaN(coords4326[0]) &&
      !isNaN(coords4326[1])
    ) {
      setNTSgeojson(featureCollection([point(coords4326)]));
    } else {
      setNTSgeojson(featureCollection([]));
    }
  }, [coords4326]);

  const addMapboxPointLayer = useCallback(() => {
    if (!props.map.getSource('nts-point')) {
      props.map.addSource('nts-point', {
        type: 'geojson',
        data: NTSgeojson,
        generateId: true,
      });

      [{ 'nts-point-icon': NTSPoint }].forEach((icon) => {
        props.map.loadImage(Object.values(icon)[0], (error, image) => {
          if (error) throw error;
          props.map.addImage(Object.keys(icon)[0], image);
        });
      });

      props.map.addLayer({
        id: 'nts-point-layer',
        type: 'symbol',
        source: 'nts-point',
        layout: {
          'icon-image': 'nts-point-icon',
          'icon-size': 1,
          'icon-offset': [0, -12],
          'icon-allow-overlap': true,
        },
      });
    } else {
      props.map.getSource('nts-point').setData(NTSgeojson);
    }
  }, [props?.map, NTSgeojson]);

  useEffect(() => {
    if (props?.coords3857 && props?.coords3857[0] && props?.coords3857[1] && props?.zone) {
      const convertedCoords = convertCoords3857To4326(props?.coords3857, props?.zone);
      if (convertedCoords && convertedCoords[0] !== null && convertedCoords[1] !== null) {
        if (
          !coords4326 ||
          Math.abs(coords4326[0] - convertedCoords[0]) > 0.0001 ||
          Math.abs(coords4326[1] - convertedCoords[1]) > 0.0001
        ) {
          setCoords4326(convertedCoords);
        }
      } else {
        setCoords4326(null);
      }
    } else {
      if (coords4326 !== null) {
        setCoords4326(null);
      }
    }
  }, [props?.coords3857, props?.zone, coords4326]);

  useEffect(() => {
    updateGeojson();
  }, [updateGeojson]);

  useEffect(() => {
    updateFormDataWithCoords(coords4326);
  }, [coords4326, updateFormDataWithCoords]);

  useEffect(() => {
    if (!props.map) return;

    const handleMapClick = (e) => {
      if (!props.zone) return;
      const clickCoords4326 = [Number(e.lngLat.lng), Number(e.lngLat.lat)];
      const clickCoords3857 = convertCoords4326To3857(clickCoords4326, props.zone);

      props.setCoords3857(clickCoords3857);
    };

    props.map.on('click', handleMapClick);

    return () => {
      props.map.off('click', handleMapClick);
    };
  }, [props?.map, props?.zone, props?.setCoords3857]);

  useEffect(() => {
    if (!props.map) return;
    addMapboxPointLayer();
  }, [props?.map, addMapboxPointLayer]);

  return (
    <div className="flex-column-group no-y-margin">
      <div style={{ position: 'relative' }}>
        <HASwitchers
          {...props?.haReportsNonTechnical}
          googleWms={true}
          style={{
            position: 'absolute',
            top: '66px',
            zIndex: 1,
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '4px',
            padding: '0px 15px 7px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            left: '0px',
            borderRadius: '0 0 8px',
            boxShadow: '0px 20px 25px 0px rgba(76, 103, 100, 0.1)',
          }}
        />
        <Map
          map={props?.map}
          setMap={props?.setMap}
          haReportsPolygons={props?.haReportsNonTechnical?.haReportsPolygons}
          // azureLayerEnabled={props?.haReportsNonTechnical?.azureLayerEnabled}
          // setAzureLayerEnabled={props?.haReportsNonTechnical?.setAzureLayerEnabled}
          googleWms
        />
      </div>
      <div className="flex-group">
        <div className={`double-text ${props?.errors?.point?.coordinates ? 'error' : ''}`}>
          <span>Координати</span>
          <span>Coordinates</span>
        </div>
        <div className="line" />
        <div className="flex-group">
          <input
            placeholder="440571"
            id="nts-map-long"
            className="text-center"
            value={props?.coords3857 ? props?.coords3857[0]?.toFixed() : null}
            autoComplete="off"
            onKeyDown={handleKeyDown}
            onChange={(e) => {
              handleLong(e.target.value);
            }}
          />
          <input
            placeholder="440571"
            id="nts-map-lat"
            className="text-center"
            value={props?.coords3857 ? props?.coords3857[1]?.toFixed() : null}
            autoComplete="off"
            onKeyDown={handleKeyDown}
            onChange={(e) => {
              handleLat(e.target.value);
            }}
          />
        </div>
      </div>
    </div>
  );
};
